import { useForm } from '@inertiajs/react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  LINKEDIN_PROFILE_URL_REGEX,
  normalizeLinkedInProfileUrl,
  normalizeXProfileUrl,
  X_PROFILE_URL_REGEX,
} from '@/lib/validations/social-profiles';
import type { OnboardingEdition } from '@/types';
import { errorMessages } from './errors';
import {
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  GlobalError,
} from './form-field';
import { OnboardingShell, titleFont } from './OnboardingShell';

// social-profiles/_components/social-profiles-form.tsx (26-ar only).
export function SocialProfilesForm({
  edition,
  defaultLinkedin,
  defaultX,
}: {
  edition: OnboardingEdition;
  defaultLinkedin: string;
  defaultX: string;
}) {
  const form = useForm({ linkedin: defaultLinkedin, x: defaultX });
  const { data, setData, post, processing } = form;
  const errors = errorMessages(form.errors);

  const isValid =
    LINKEDIN_PROFILE_URL_REGEX.test(
      normalizeLinkedInProfileUrl(data.linkedin.trim()),
    ) && X_PROFILE_URL_REGEX.test(normalizeXProfileUrl(data.x.trim()));

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    post(`${edition.basePath}/social-profiles`, { preserveScroll: true });
  };

  return (
    <OnboardingShell ph26={edition.ph26} width="max-w-2xl">
      <div className="text-center">
        <h1
          className={`inline-block font-bold ${titleFont(edition.ph26)} text-2xl text-primary sm:text-3xl md:text-4xl`}
        >
          Tu{' '}
          <span className="bg-primary px-2 py-1 text-background">
            perfil público
          </span>
        </h1>
        <p className="mt-4 font-mono text-primary/70 text-sm">
          Para que otros hackers, mentores y sponsors puedan encontrarte
        </p>
      </div>

      <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
        <form onSubmit={onSubmit} className="space-y-6">
          <FormItem>
            <FormLabel htmlFor="linkedin" error={!!errors.linkedin}>
              LinkedIn *
            </FormLabel>
            <Input
              id="linkedin"
              type="url"
              placeholder="https://linkedin.com/in/tu-usuario"
              disabled={processing}
              value={data.linkedin}
              onChange={(e) => setData('linkedin', e.target.value)}
            />
            <FormDescription>
              Pega la URL completa de tu perfil de LinkedIn
            </FormDescription>
            <FormMessage>{errors.linkedin}</FormMessage>
          </FormItem>

          <FormItem>
            <FormLabel htmlFor="x" error={!!errors.x}>
              X / Twitter *
            </FormLabel>
            <Input
              id="x"
              type="url"
              placeholder="https://x.com/tu-usuario"
              disabled={processing}
              value={data.x}
              onChange={(e) => setData('x', e.target.value)}
            />
            <FormDescription>
              Pega la URL de tu perfil de X (o twitter.com — lo normalizamos)
            </FormDescription>
            <FormMessage>{errors.x}</FormMessage>
          </FormItem>

          <GlobalError>{errors.base}</GlobalError>

          <div className="flex justify-center pt-4">
            <Button
              type="submit"
              size="lg"
              disabled={processing || !isValid}
              className="w-full sm:w-auto"
            >
              {processing ? 'Guardando...' : 'Continuar'}
            </Button>
          </div>
        </form>
      </div>
    </OnboardingShell>
  );
}
